<template>
  <div
    class="relative flex flex-col min-w-0 break-words w-full mb-6 shadow-lg rounded-lg bg-white"
  >
    <!-- Header -->
    <div class="rounded-t-lg bg-white mb-0 px-6 py-6">
      <div class="text-center flex justify-between">
        <h6 class="text-gray-800 text-xl font-bold">
          Terms of Service Agreement
        </h6>
      </div>
    </div>

    <!-- Terms Content -->
    <div class="flex-auto px-4 lg:px-10 py-10 pt-0 max-h-96 overflow-y-auto">
      <div class="terms-content text-sm text-gray-700 leading-relaxed">
        <!-- Introduction -->
        <div class="mb-6">
          <p class="mb-4">
            This <strong>TERMS OF SERVICE AGREEMENT</strong> (this "Agreement")
            is entered into by and between <strong>Blessed FMS Inc.</strong>, a
            Financial Management Service company ("Blessed FMS Inc.") and any
            person who consents to this Agreement and the entity that such
            person represents (together, "User"). Each of Blessed FMS Inc. and
            User are referred to as a "Party" and are collectively referred to
            as the "Parties."
          </p>

          <div class="bg-blue-50 border-l-4 border-blue-400 p-4 rounded mb-4">
            <p class="text-blue-800 font-semibold">
              BY LOGGING INTO, ACCESSING, VIEWING, DOWNLOADING, OR OTHERWISE
              USING ANY PART OF BLESSED FMS INC.'S NETWORK, SYSTEMS OR RESOURCES
              (the "SERVICE"), USER EXPRESSLY AGREES TO AND CONSENTS TO BE BOUND
              BY ALL OF THE TERMS OF THIS AGREEMENT.
            </p>
          </div>
        </div>

        <!-- Section 1: Service Terms and Limitations -->
        <div class="mb-6">
          <h6 class="text-gray-800 text-base font-bold mb-3 uppercase">
            1. Service Terms and Limitations
          </h6>

          <div class="ml-4">
            <div class="mb-4">
              <h6 class="text-gray-700 font-semibold mb-2">A. Description</h6>
              <p>
                Blessed FMS Inc.'s network, systems, and resources (the
                "Service") is proprietary to Blessed FMS Inc. and is protected
                by intellectual property laws and treaties. User's access to the
                Service is licensed and not sold.
              </p>
            </div>

            <div class="mb-4">
              <h6 class="text-gray-700 font-semibold mb-2">B. Limitations</h6>
              <p class="mb-2">User shall not:</p>
              <ul class="list-disc list-inside ml-4 space-y-1">
                <li>
                  Sell, market, rent, sub-license, or license any aspect of the
                  service
                </li>
                <li>
                  Decipher, decompile, disassemble, reverse engineer the Service
                </li>
                <li>Allow unauthorized access to the Service</li>
                <li>
                  Write or develop any derivative works based upon the Service
                </li>
                <li>
                  Modify, adapt, or translate the Service without prior written
                  consent
                </li>
                <li>
                  Use the Service to provide processing services to third
                  parties
                </li>
                <li>
                  Remove identification, patent, copyright, or trademark notices
                </li>
                <li>Circumvent any security feature of the Service</li>
                <li>
                  Use the Service in any manner that violates applicable law
                </li>
              </ul>
            </div>
          </div>
        </div>

        <!-- Section 2: Privacy and Security -->
        <div class="mb-6">
          <h6 class="text-gray-800 text-base font-bold mb-3 uppercase">
            2. Privacy and Security
          </h6>

          <div class="ml-4 space-y-4">
            <div>
              <h6 class="text-gray-700 font-semibold mb-2">A. Security</h6>
              <p>
                User shall be solely responsible for the security,
                confidentiality, and integrity of all messages, content, and
                other information. User agrees to bear all responsibility for
                the confidentiality of User's password and all use incurred from
                use of the Service.
              </p>
            </div>

            <div>
              <h6 class="text-gray-700 font-semibold mb-2">B. Privacy</h6>
              <p class="mb-2">
                Blessed FMS Inc. will attempt to respect User's privacy but may
                disclose information when necessary to:
              </p>
              <ul class="list-disc list-inside ml-4 space-y-1">
                <li>Comply with legal process or governmental requirements</li>
                <li>
                  Protect and defend the rights or property of Blessed FMS Inc.
                </li>
                <li>Enforce this Agreement</li>
                <li>Protect the interests of other users</li>
                <li>Operate or conduct maintenance of services</li>
              </ul>
            </div>

            <div>
              <h6 class="text-gray-700 font-semibold mb-2">
                C. Confidentiality
              </h6>
              <div
                class="bg-yellow-50 border-l-4 border-yellow-400 p-3 rounded"
              >
                <p class="text-yellow-800">
                  <strong>Important:</strong> Information accessed is protected
                  by federal and state privacy laws, including HIPAA and FERPA.
                  Violation may carry criminal and civil penalties.
                </p>
              </div>
            </div>
          </div>
        </div>

        <!-- Section 3: User Representations -->
        <div class="mb-6">
          <h6 class="text-gray-800 text-base font-bold mb-3 uppercase">
            3. User Representations
          </h6>
          <div class="ml-4">
            <p class="mb-2">User represents and warrants that:</p>
            <ul class="list-disc list-inside ml-4 space-y-1">
              <li>User is over the age of eighteen (18)</li>
              <li>User has power and authority to enter into this Agreement</li>
              <li>
                All information provided is truthful, accurate, and complete
              </li>
              <li>User shall comply with all terms and conditions</li>
              <li>User has provided accurate registration information</li>
            </ul>
          </div>
        </div>

        <!-- Section 4: Acceptable Use -->
        <div class="mb-6">
          <h6 class="text-gray-800 text-base font-bold mb-3 uppercase">
            4. Acceptable Use
          </h6>
          <div class="ml-4">
            <p class="mb-2">User agrees not to use the Service to:</p>
            <ul class="list-disc list-inside ml-4 space-y-1">
              <li>Violate any law or regulation</li>
              <li>Disseminate unsolicited messages or commercial email</li>
              <li>Transmit abusive, obscene, or threatening material</li>
              <li>Infringe intellectual property rights</li>
              <li>Create false identities</li>
              <li>
                Interfere with or gain unauthorized access to other accounts
              </li>
              <li>Transmit viruses or malicious code</li>
              <li>Damage or impair Blessed FMS Inc.'s servers</li>
            </ul>
          </div>
        </div>

        <!-- Additional Sections -->
        <div class="mb-6">
          <h6 class="text-gray-800 text-base font-bold mb-3 uppercase">
            5. Additional Terms
          </h6>
          <div class="ml-4 space-y-3">
            <div>
              <span class="font-semibold">Termination:</span> This Agreement
              continues until terminated. Blessed FMS Inc. reserves the right to
              suspend or terminate access at any time.
            </div>
            <div>
              <span class="font-semibold">Disclaimer:</span> The Service is
              provided "AS IS" without warranty. Use is at User's sole risk.
            </div>
            <div>
              <span class="font-semibold">Liability Limitation:</span> Blessed
              FMS Inc.'s total liability shall not exceed $5,000.
            </div>
            <div>
              <span class="font-semibold">Governing Law:</span> This Agreement
              is governed by the laws of the Commonwealth of Massachusetts.
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Acceptance Section -->
    <div class="border-t border-gray-200 px-4 lg:px-10 py-6">
      <div class="flex items-center mb-4">
        <input
          id="accept-terms"
          v-model="termsAccepted"
          type="checkbox"
          class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
          required
        />
        <label for="accept-terms" class="ml-2 block text-sm text-gray-700">
          I acknowledge that I have read, understood, and agree to be bound by
          these Terms of Service
        </label>
      </div>

      <div class="flex justify-left space-x-3">
        <!-- <button
          type="button"
          @click="declineTerms"
          class="bg-red-600 text-white active:bg-gray-700 text-sm font-bold uppercase px-6 py-3 rounded shadow hover:shadow-lg outline-none focus:outline-none mr-3"
          style="transition: all 0.15s ease 0s"
        >
          Decline
        </button> -->
        <button
          type="button"
          @click="acceptTerms"
          class="bg-lightBlue-500 text-white active:bg-blue-600 text-sm font-bold uppercase px-6 py-3 rounded shadow hover:shadow-lg outline-none focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
          style="transition: all 0.15s ease 0s"
        >
          Accept Terms
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import Swal from "sweetalert2/dist/sweetalert2.js";
import axios from "axios";
export default {
  name: "TermsAndConditions",
  props: {
    accepted: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      termsAccepted: this.accepted,
    };
  },
  methods: {
    async acceptTerms() {
      if (!this.termsAccepted) {
        Swal.fire(
          "Error",
          "Please check the acknowledge box to continue...",
          "error"
        );
        return;
      }
      try {
        const token = localStorage.getItem("token");
        const response = await axios.get(
          `${process.env.VUE_APP_BACKEND_DOMAIN}/api/client_service_agreement_policy`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        this.$emit("terms-updated");
        Swal.fire("Success", "Agreement Success", "success");

        if (response.data.status === "error") {
          this.errors = response.data.errors;
        } else {
          this.errors = {};
        }
      } catch (error) {
        console.error("Backend error:", error);
      }
    },

    declineTerms() {
      console.log("Terms declined by user");

      if (
        confirm(
          "Are you sure you want to decline the Terms of Service? You will not be able to use the service without accepting these terms."
        )
      ) {
        this.termsAccepted = false;

        // Emit event to parent component
        this.$emit("terms-declined", {
          accepted: false,
          timestamp: new Date().toISOString(),
        });

        // Optionally redirect or close
        alert(
          "Terms of Service declined. Please contact support if you have questions."
        );
      }
    },
  },

  mounted() {
    console.log("TermsAndConditions component mounted");
  },
};
</script>

<style scoped>
.terms-content {
  line-height: 1.6;
}

.terms-content h6 {
  margin-top: 1rem;
  margin-bottom: 0.5rem;
}

.terms-content ul {
  margin-bottom: 1rem;
}

.terms-content li {
  margin-bottom: 0.25rem;
}

/* Custom scrollbar for the terms content */
.overflow-y-auto::-webkit-scrollbar {
  width: 6px;
}

.overflow-y-auto::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.overflow-y-auto::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.overflow-y-auto::-webkit-scrollbar-thumb:hover {
  background: #a1a1a1;
}
</style>
